import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import { formToObject } from 'lib/forms/formToObject'
import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import ExperienceDetailsTransferBookingTravellers from './ExperienceTransferBookingTravellers'
import ExperienceTransferBookingOption from './ExperienceTransferBookingOption'
import ExperienceTransferBookingForm from './ExperienceTransferBookingForm'
import type { SingleTransferForm } from './ExperienceTransferUtils'
import { findBestTransfer } from './ExperienceTransferUtils'
import noop from 'lib/function/noop'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ExperienceTransferBookingOptionError from './ExperienceTransferBookingOptionError'
import ExperienceDetailsTransferBookingBaggage from './ExperienceTransferBookingBaggage'
import Heading from 'LuxKit/Typography/Heading'

interface Props {
  defaultTransfer: App.ExperienceTransferView
  transferOptions: Array<App.ExperienceTransferOption>
  experience: App.ExperienceBookingView
  hasLoyaltyBenefit?: boolean
  formId?: string
  onPriceChange?: (total: number, totalMember: number, totalGrossPrice: number, isFromPrice?: boolean) => void
  onSubmit?: (transfer: Array<App.ExperienceTransferView>) => void
}

function ExperienceTransferSingleBooking(props: Props) {
  const {
    defaultTransfer,
    transferOptions,
    experience,
    hasLoyaltyBenefit,
    formId,
    onPriceChange = noop,
    onSubmit = noop,
  } = props

  const intl = useIntl()

  const hotelDropOffLabel = intl.formatMessage({
    id: 'deBwZuRmX5ZQxpQPmweY',
    defaultMessage: 'Your hotel drop off transfer',
    description: 'Label for airport-to-hotel transfer result summary',
  })
  const airportDropOffLabel = intl.formatMessage({
    id: 'pffA4ozABnUxjjR1inoc',
    defaultMessage: 'Your airport drop off transfer',
    description: 'Label for hotel-to-airport transfer result summary',
  })

  const availableOptions = useMemo(() => transferOptions.filter(option => option.transferTypes.includes(defaultTransfer.type)), [defaultTransfer.type, transferOptions])
  const [transfer, setTransfer] = useState<App.ExperienceTransferView>(defaultTransfer)

  const hasTicketValues = !!transfer?.hotel.kmFromAirport

  // Getting the best option for the user
  const { bestTransfer, noTransferReason } = useMemo(() => {
    return findBestTransfer(transfer, availableOptions)
  }, [transfer, availableOptions])

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formValues = formToObject<SingleTransferForm>(e.currentTarget)

    onSubmit([{
      ...transfer,
      travellers: {
        ...transfer.travellers,
        // if we have no children, reset the seats. We only kept them around in case the user
        // flicked back and forth between having children and not for convenience
        boosterSeats: transfer.travellers.children ? transfer.travellers.boosterSeats : 0,
        childSeats: transfer.travellers.children ? transfer.travellers.childSeats : 0,
      },
      type: formValues.transfer.type,
      flightNumber: formValues.transfer.flightNumber,
      time: formValues.transfer.time,
      date: formValues.transfer.date,
      option: bestTransfer,
      title: experience.title,
    }])
  }

  const totalPrice = bestTransfer?.price ?? experience.price ?? 0
  const totalMemberPrice = bestTransfer?.memberPrice ?? experience.pricing.memberPrice ?? 0
  const totalGrossPrice = (totalPrice && bestTransfer?.discounts && bestTransfer.discounts.base.amount > 0) ? totalPrice + bestTransfer.discounts.base.amount : totalPrice

  useEffect(() => {
    onPriceChange(totalPrice, totalMemberPrice, totalGrossPrice)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPrice, totalMemberPrice, totalGrossPrice])

  return <VerticalSpacer gap={24} as="form" onSubmit={onFormSubmit} id={formId}>
    <VerticalSpacer gap={16}>
      <Heading variant="heading5">
        {intl.formatMessage({
          id: 'xIp4aDDzYLucxOLVMD_t',
          defaultMessage: 'Hotel Transfer',
          description: 'Heading for hotel transfer booking section in the booking flow',
        })}
      </Heading>
      <ExperienceTransferBookingForm
        transfer={transfer}
        name="transfer"
        onChange={setTransfer}
        experience={experience.originalExperience}
      />
      {/* Cast transfer for child components - they don't use the type field */}
      <ExperienceDetailsTransferBookingTravellers
        name="transfer"
        options={availableOptions}
        transfer={transfer}
        onChange={setTransfer}
      />
      <ExperienceDetailsTransferBookingBaggage
        name="transfer"
        options={availableOptions}
        transfer={transfer}
        onChange={setTransfer}
      />
    </VerticalSpacer>
    {hasTicketValues && <>
      <Divider kind="primary" />
      <VerticalSpacer gap={12} style={{ position: 'relative' }}>
        <BodyText variant="large" weight="bold">{transfer.type === 'AIRPORT-TO-HOTEL' ? hotelDropOffLabel : airportDropOffLabel}</BodyText>
        {bestTransfer && <ExperienceTransferBookingOption transferTicket={bestTransfer} hasLoyaltyBenefit={hasLoyaltyBenefit} />}
        {noTransferReason && <ExperienceTransferBookingOptionError reason={noTransferReason} />}
        <HiddenInput
          required={!bestTransfer}
          invalidMessage={intl.formatMessage({
            id: 'cGzuZ5fhZbdaB87N4k2o',
            defaultMessage: 'Please adjust your search parameters and try again.',
            description: 'Error message when no transfer option matches the search criteria',
          })}
        />
      </VerticalSpacer>
    </>}
  </VerticalSpacer>
}

export default ExperienceTransferSingleBooking
